'use client';
import React, { useState } from 'react';
import { Radio, Plus, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Activity, Shield, ArrowLeftRight, UserCheck, AlertCircle, RotateCcw, Filter, Search, ExternalLink, Copy, Check } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type StreamType = 'settlement' | 'kyc' | 'aml';
type SubscriberRole = 'regulator' | 'custodian' | 'auditor';
type DeliveryStatus = 'delivered' | 'failed' | 'pending' | 'retrying';
type SubStatus = 'active' | 'paused' | 'suspended';

interface EventStream {
  id: string;
  type: StreamType;
  label: string;
  description: string;
  icon: React.ElementType;
  eventCount: number;
  lastEvent: string;
  color: string;
}

interface Subscription {
  id: string;
  subscriberName: string;
  subscriberRole: SubscriberRole;
  streams: StreamType[];
  endpointUrl: string;
  status: SubStatus;
  createdAt: string;
  lastDelivery: string;
  totalDelivered: number;
  totalFailed: number;
  retryCount: number;
  secret: string;
  filterSeverity: string[];
}

interface DeliveryEvent {
  id: string;
  subscriptionId: string;
  subscriberName: string;
  streamType: StreamType;
  eventType: string;
  payload: string;
  status: DeliveryStatus;
  attemptCount: number;
  maxRetries: number;
  nextRetryAt: string | null;
  deliveredAt: string | null;
  failureReason: string | null;
  httpStatus: number | null;
  timestamp: string;
  latencyMs: number | null;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const eventStreams: EventStream[] = [
  {
    id: 'stream-settlement',
    type: 'settlement',
    label: 'Settlement Events',
    description: 'Escrow releases, batch settlements, payment confirmations, reconciliation outcomes, and settlement failures.',
    icon: ArrowLeftRight,
    eventCount: 1284,
    lastEvent: '2026-08-21 01:08:44',
    color: 'text-info',
  },
  {
    id: 'stream-kyc',
    type: 'kyc',
    label: 'KYC / KYB Events',
    description: 'Identity verification completions, KYC approvals, rejections, EDD escalations, and periodic refresh triggers.',
    icon: UserCheck,
    eventCount: 3471,
    lastEvent: '2026-08-21 01:12:19',
    color: 'text-positive',
  },
  {
    id: 'stream-aml',
    type: 'aml',
    label: 'AML / CFT Events',
    description: 'Transaction screening hits, STR/SAR filings, sanctions matches, PEP flags, and rule-triggered escalations.',
    icon: Shield,
    eventCount: 892,
    lastEvent: '2026-08-21 01:14:55',
    color: 'text-critical',
  },
];

const subscriptions: Subscription[] = [
  {
    id: 'sub-001',
    subscriberName: 'FCA Regulatory Gateway',
    subscriberRole: 'regulator',
    streams: ['aml', 'kyc'],
    endpointUrl: 'https://gateway.fca.org.uk/crowdvault/events',
    status: 'active',
    createdAt: '2026-07-01',
    lastDelivery: '2026-08-21 01:14:55',
    totalDelivered: 4218,
    totalFailed: 12,
    retryCount: 3,
    secret: 'whsec_fca_••••••••••••••••',
    filterSeverity: ['critical', 'high'],
  },
  {
    id: 'sub-002',
    subscriberName: 'Barclays Escrow Services',
    subscriberRole: 'custodian',
    streams: ['settlement'],
    endpointUrl: 'https://api.barclays-escrow.com/webhooks/crowdvault',
    status: 'active',
    createdAt: '2026-07-15',
    lastDelivery: '2026-08-21 01:08:44',
    totalDelivered: 1271,
    totalFailed: 3,
    retryCount: 3,
    secret: 'whsec_barc_••••••••••••••••',
    filterSeverity: ['critical', 'high', 'medium'],
  },
  {
    id: 'sub-003',
    subscriberName: 'Deloitte External Audit',
    subscriberRole: 'auditor',
    streams: ['settlement', 'kyc', 'aml'],
    endpointUrl: 'https://audit-api.deloitte.com/ingest/crowdvault',
    status: 'active',
    createdAt: '2026-08-01',
    lastDelivery: '2026-08-21 01:12:19',
    totalDelivered: 2104,
    totalFailed: 7,
    retryCount: 5,
    secret: 'whsec_del_••••••••••••••••',
    filterSeverity: ['critical', 'high', 'medium', 'low'],
  },
  {
    id: 'sub-004',
    subscriberName: 'HSBC Trustee Services',
    subscriberRole: 'custodian',
    streams: ['settlement', 'kyc'],
    endpointUrl: 'https://trustee-api.hsbc.com/events/crowdvault',
    status: 'paused',
    createdAt: '2026-06-20',
    lastDelivery: '2026-08-19 14:30:00',
    totalDelivered: 988,
    totalFailed: 41,
    retryCount: 3,
    secret: 'whsec_hsbc_••••••••••••••••',
    filterSeverity: ['critical', 'high'],
  },
  {
    id: 'sub-005',
    subscriberName: 'HMRC Transaction Reporting',
    subscriberRole: 'regulator',
    streams: ['settlement', 'aml'],
    endpointUrl: 'https://reporting.hmrc.gov.uk/api/crowdvault',
    status: 'suspended',
    createdAt: '2026-05-10',
    lastDelivery: '2026-08-15 09:00:00',
    totalDelivered: 3102,
    totalFailed: 188,
    retryCount: 3,
    secret: 'whsec_hmrc_••••••••••••••••',
    filterSeverity: ['critical'],
  },
];

const deliveryEvents: DeliveryEvent[] = [
  { id: 'evt-001', subscriptionId: 'sub-001', subscriberName: 'FCA Regulatory Gateway', streamType: 'aml', eventType: 'aml.str_filed', payload: '{"alert_id":"AML-2026-0441","type":"STR","amount":480000,"currency":"GBP"}', status: 'delivered', attemptCount: 1, maxRetries: 3, nextRetryAt: null, deliveredAt: '2026-08-21 01:14:55', failureReason: null, httpStatus: 200, timestamp: '2026-08-21 01:14:54', latencyMs: 312 },
  { id: 'evt-002', subscriptionId: 'sub-003', subscriberName: 'Deloitte External Audit', streamType: 'kyc', eventType: 'kyc.approved', payload: '{"kyc_id":"KYC-2026-1882","entity":"Apex Capital Partners","risk_level":"medium"}', status: 'delivered', attemptCount: 1, maxRetries: 5, nextRetryAt: null, deliveredAt: '2026-08-21 01:12:19', failureReason: null, httpStatus: 200, timestamp: '2026-08-21 01:12:18', latencyMs: 198 },
  { id: 'evt-003', subscriptionId: 'sub-002', subscriberName: 'Barclays Escrow Services', streamType: 'settlement', eventType: 'settlement.batch_completed', payload: '{"batch_id":"SB-20260821-005","amount":2850000,"investors":28}', status: 'delivered', attemptCount: 1, maxRetries: 3, nextRetryAt: null, deliveredAt: '2026-08-21 01:08:44', failureReason: null, httpStatus: 200, timestamp: '2026-08-21 01:08:43', latencyMs: 441 },
  { id: 'evt-004', subscriptionId: 'sub-005', subscriberName: 'HMRC Transaction Reporting', streamType: 'aml', eventType: 'aml.threshold_breach', payload: '{"txn_id":"TXN-883104","amount":480000,"rule":"THRESHOLD_10K"}', status: 'failed', attemptCount: 3, maxRetries: 3, nextRetryAt: null, deliveredAt: null, failureReason: 'Connection timeout after 30s — endpoint unreachable', httpStatus: null, timestamp: '2026-08-21 00:58:11', latencyMs: null },
  { id: 'evt-005', subscriptionId: 'sub-004', subscriberName: 'HSBC Trustee Services', streamType: 'settlement', eventType: 'settlement.escrow_released', payload: '{"batch_id":"SB-20260820-004","amount":7840000,"campaign":"ClearPath Clean Energy Fund III"}', status: 'failed', attemptCount: 2, maxRetries: 3, nextRetryAt: '2026-08-21 01:30:00', deliveredAt: null, failureReason: 'HTTP 503 — Service Unavailable', httpStatus: 503, timestamp: '2026-08-21 00:45:22', latencyMs: null },
  { id: 'evt-006', subscriptionId: 'sub-001', subscriberName: 'FCA Regulatory Gateway', streamType: 'kyc', eventType: 'kyc.edd_escalated', payload: '{"kyc_id":"KYC-2026-1879","entity":"Unknown Beneficial Owner Entity","reason":"UBO_UNVERIFIED"}', status: 'retrying', attemptCount: 1, maxRetries: 3, nextRetryAt: '2026-08-21 01:20:00', deliveredAt: null, failureReason: 'HTTP 429 — Rate limit exceeded', httpStatus: 429, timestamp: '2026-08-21 00:40:07', latencyMs: null },
  { id: 'evt-007', subscriptionId: 'sub-003', subscriberName: 'Deloitte External Audit', streamType: 'settlement', eventType: 'settlement.discrepancy_flagged', payload: '{"batch_id":"SB-20260818-002","variance":1200,"currency":"GBP"}', status: 'delivered', attemptCount: 1, maxRetries: 5, nextRetryAt: null, deliveredAt: '2026-08-20 17:00:12', failureReason: null, httpStatus: 200, timestamp: '2026-08-20 17:00:11', latencyMs: 267 },
  { id: 'evt-008', subscriptionId: 'sub-002', subscriberName: 'Barclays Escrow Services', streamType: 'settlement', eventType: 'settlement.payment_rejected', payload: '{"batch_id":"SB-20260817-001","reason":"BENEFICIARY_MISMATCH"}', status: 'pending', attemptCount: 0, maxRetries: 3, nextRetryAt: '2026-08-21 01:18:00', deliveredAt: null, failureReason: null, httpStatus: null, timestamp: '2026-08-21 01:15:00', latencyMs: null },
  { id: 'evt-009', subscriptionId: 'sub-001', subscriberName: 'FCA Regulatory Gateway', streamType: 'aml', eventType: 'aml.sanctions_match', payload: '{"screening_id":"SCR-2026-0882","entity":"Greenfield Ventures Ltd","list":"OFAC"}', status: 'delivered', attemptCount: 1, maxRetries: 3, nextRetryAt: null, deliveredAt: '2026-08-20 11:30:44', failureReason: null, httpStatus: 200, timestamp: '2026-08-20 11:30:43', latencyMs: 388 },
  { id: 'evt-010', subscriptionId: 'sub-003', subscriberName: 'Deloitte External Audit', streamType: 'aml', eventType: 'aml.pep_flagged', payload: '{"investor_id":"INV-2026-0441","name":"Pemberton Asset Management","pep_category":"TIER_2"}', status: 'delivered', attemptCount: 2, maxRetries: 5, nextRetryAt: null, deliveredAt: '2026-08-20 09:15:22', failureReason: null, httpStatus: 200, timestamp: '2026-08-20 09:14:55', latencyMs: 512 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const streamBadge: Record<StreamType, string> = {
  settlement: 'bg-info/10 text-info border border-info/30',
  kyc: 'bg-positive/10 text-positive border border-positive/30',
  aml: 'bg-critical/10 text-critical border border-critical/30',
};

const streamLabel: Record<StreamType, string> = {
  settlement: 'Settlement',
  kyc: 'KYC / KYB',
  aml: 'AML / CFT',
};

const roleBadge: Record<SubscriberRole, string> = {
  regulator: 'bg-accent/10 text-accent border border-accent/30',
  custodian: 'bg-warning/10 text-warning border border-warning/30',
  auditor: 'bg-muted/20 text-muted-foreground border border-border',
};

const roleLabel: Record<SubscriberRole, string> = {
  regulator: 'Regulator',
  custodian: 'Custodian',
  auditor: 'Auditor',
};

const subStatusCfg: Record<SubStatus, { label: string; cls: string; icon: React.ElementType }> = {
  active:    { label: 'Active',    cls: 'bg-positive/10 text-positive border border-positive/30',  icon: CheckCircle },
  paused:    { label: 'Paused',    cls: 'bg-warning/10 text-warning border border-warning/30',      icon: Clock },
  suspended: { label: 'Suspended', cls: 'bg-critical/10 text-critical border border-critical/30',   icon: XCircle },
};

const deliveryCfg: Record<DeliveryStatus, { label: string; cls: string; icon: React.ElementType }> = {
  delivered: { label: 'Delivered', cls: 'bg-positive/10 text-positive border border-positive/30',  icon: CheckCircle },
  failed:    { label: 'Failed',    cls: 'bg-critical/10 text-critical border border-critical/30',   icon: XCircle },
  pending:   { label: 'Pending',   cls: 'bg-warning/10 text-warning border border-warning/30',      icon: Clock },
  retrying:  { label: 'Retrying',  cls: 'bg-accent/10 text-accent border border-accent/30',         icon: RotateCcw },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StreamCard({ stream }: { stream: EventStream }) {
  const Icon = stream.icon;
  return (
    <div className="glass-card rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-surface-3 ${stream.color}`}>
          <Icon size={18} />
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${streamBadge[stream.type]}`}>
          {streamLabel[stream.type]}
        </span>
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">{stream.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{stream.description}</p>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-border">
        <div>
          <p className="text-xs text-muted-foreground">Total Events</p>
          <p className="text-base font-bold font-mono-data text-foreground">{stream.eventCount.toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Last Event</p>
          <p className="text-xs font-mono-data text-muted-foreground">{stream.lastEvent.split(' ')[1]}</p>
        </div>
      </div>
    </div>
  );
}

function SubscriptionRow({ sub, onToggle }: { sub: Subscription; onToggle: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const cfg = subStatusCfg[sub.status];
  const StatusIcon = cfg.icon;
  const deliveryRate = sub.totalDelivered + sub.totalFailed > 0
    ? ((sub.totalDelivered / (sub.totalDelivered + sub.totalFailed)) * 100).toFixed(1)
    : '—';

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-surface-3/40 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground truncate">{sub.subscriberName}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleBadge[sub.subscriberRole]}`}>
              {roleLabel[sub.subscriberRole]}
            </span>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
              <StatusIcon size={10} />
              {cfg.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {sub.streams.map(s => (
              <span key={s} className={`text-xs px-1.5 py-0.5 rounded ${streamBadge[s]}`}>{streamLabel[s]}</span>
            ))}
          </div>
        </div>
        <div className="hidden md:flex items-center gap-6 text-right flex-shrink-0">
          <div>
            <p className="text-xs text-muted-foreground">Delivery Rate</p>
            <p className="text-sm font-bold font-mono-data text-foreground">{deliveryRate}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Delivered</p>
            <p className="text-sm font-mono-data text-positive">{sub.totalDelivered.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Failed</p>
            <p className="text-sm font-mono-data text-critical">{sub.totalFailed}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(sub.id); }}
            className={`btn-ghost p-1.5 text-xs rounded-lg ${sub.status === 'active' ? 'text-warning hover:bg-warning/10' : 'text-positive hover:bg-positive/10'}`}
            title={sub.status === 'active' ? 'Pause subscription' : 'Resume subscription'}
          >
            {sub.status === 'active' ? <Clock size={14} /> : <Activity size={14} />}
          </button>
          {expanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border px-4 py-4 bg-surface-3/20 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Endpoint URL</p>
              <div className="flex items-center gap-2 bg-surface-3 rounded-lg px-3 py-2">
                <code className="text-xs font-mono-data text-foreground flex-1 truncate">{sub.endpointUrl}</code>
                <a href={sub.endpointUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Signing Secret</p>
              <div className="flex items-center gap-2 bg-surface-3 rounded-lg px-3 py-2">
                <code className="text-xs font-mono-data text-muted-foreground flex-1">{sub.secret}</code>
                <button onClick={handleCopy} className="text-muted-foreground hover:text-foreground">
                  {copied ? <Check size={12} className="text-positive" /> : <Copy size={12} />}
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-surface-3 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Retry Policy</p>
              <p className="text-sm font-semibold text-foreground mt-0.5">Max {sub.retryCount} retries</p>
              <p className="text-xs text-muted-foreground">Exponential backoff</p>
            </div>
            <div className="bg-surface-3 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Severity Filter</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {sub.filterSeverity.map(s => (
                  <span key={s} className="text-xs bg-surface-2 border border-border rounded px-1.5 py-0.5 capitalize">{s}</span>
                ))}
              </div>
            </div>
            <div className="bg-surface-3 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Last Delivery</p>
              <p className="text-xs font-mono-data text-foreground mt-0.5">{sub.lastDelivery}</p>
            </div>
            <div className="bg-surface-3 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Subscribed Since</p>
              <p className="text-xs font-mono-data text-foreground mt-0.5">{sub.createdAt}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeliveryRow({ event, onRetry }: { event: DeliveryEvent; onRetry: (id: string) => void }) {
  const cfg = deliveryCfg[event.status];
  const StatusIcon = cfg.icon;
  const streamCls = streamBadge[event.streamType];

  return (
    <tr className="border-b border-border hover:bg-surface-3/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
            <StatusIcon size={10} />
            {cfg.label}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-xs font-semibold text-foreground">{event.subscriberName}</p>
        <p className="text-xs text-muted-foreground font-mono-data">{event.timestamp}</p>
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-1.5 py-0.5 rounded ${streamCls}`}>{streamLabel[event.streamType]}</span>
        <p className="text-xs font-mono-data text-muted-foreground mt-0.5">{event.eventType}</p>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-xs font-mono-data text-foreground">{event.attemptCount}/{event.maxRetries}</span>
      </td>
      <td className="px-4 py-3 text-center">
        {event.httpStatus ? (
          <span className={`text-xs font-mono-data ${event.httpStatus === 200 ? 'text-positive' : 'text-critical'}`}>
            {event.httpStatus}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {event.latencyMs ? (
          <span className="text-xs font-mono-data text-muted-foreground">{event.latencyMs}ms</span>
        ) : event.failureReason ? (
          <span className="text-xs text-critical truncate max-w-[160px] block" title={event.failureReason}>
            {event.failureReason}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {(event.status === 'failed' || event.status === 'retrying') && (
          <button
            onClick={() => onRetry(event.id)}
            className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent/80 font-medium transition-colors"
          >
            <RotateCcw size={12} />
            Retry
          </button>
        )}
        {event.status === 'delivered' && (
          <span className="text-xs text-muted-foreground font-mono-data">{event.deliveredAt?.split(' ')[1]}</span>
        )}
        {event.status === 'pending' && (
          <span className="text-xs text-warning font-mono-data">{event.nextRetryAt?.split(' ')[1]}</span>
        )}
      </td>
    </tr>
  );
}

// ─── Add Subscription Modal ───────────────────────────────────────────────────

function AddSubscriptionModal({ onClose }: { onClose: () => void }) {
  const [selectedStreams, setSelectedStreams] = useState<StreamType[]>([]);
  const [role, setRole] = useState<SubscriberRole>('regulator');
  const [submitted, setSubmitted] = useState(false);

  const toggleStream = (s: StreamType) => {
    setSelectedStreams(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(onClose, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 border border-border rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Plus size={18} className="text-primary" />
            <h2 className="text-base font-bold text-foreground">New Subscription</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5"><XCircle size={18} /></button>
        </div>
        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <CheckCircle size={40} className="text-positive" />
            <p className="text-sm font-semibold text-foreground">Subscription created</p>
            <p className="text-xs text-muted-foreground">Webhook endpoint will be verified shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Subscriber Name</label>
              <input required className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary" placeholder="e.g. FCA Regulatory Gateway" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Subscriber Role</label>
              <div className="flex gap-2">
                {(['regulator', 'custodian', 'auditor'] as SubscriberRole[]).map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors capitalize ${role === r ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Event Streams</label>
              <div className="flex gap-2">
                {(['settlement', 'kyc', 'aml'] as StreamType[]).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStream(s)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${selectedStreams.includes(s) ? `${streamBadge[s]} border-current` : 'border-border text-muted-foreground hover:border-primary/50'}`}
                  >
                    {streamLabel[s]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Webhook Endpoint URL</label>
              <input required type="url" className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary font-mono-data" placeholder="https://your-endpoint.example.com/events" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Max Retries</label>
                <select className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary">
                  <option>3</option><option>5</option><option>10</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Min Severity</label>
                <select className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary">
                  <option>Critical only</option><option>High+</option><option>Medium+</option><option>All events</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 btn-ghost py-2 rounded-lg text-sm">Cancel</button>
              <button type="submit" disabled={selectedStreams.length === 0} className="flex-1 btn-primary py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Create Subscription
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type Tab = 'subscriptions' | 'delivery';

export default function ComplianceEventSubscriptions() {
  const [tab, setTab] = useState<Tab>('subscriptions');
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<SubscriberRole | 'all'>('all');
  const [streamFilter, setStreamFilter] = useState<StreamType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DeliveryStatus | 'all'>('all');
  const [subs, setSubs] = useState(subscriptions);
  const [events, setEvents] = useState(deliveryEvents);

  const totalDelivered = subs.reduce((a, s) => a + s.totalDelivered, 0);
  const totalFailed = subs.reduce((a, s) => a + s.totalFailed, 0);
  const activeCount = subs.filter(s => s.status === 'active').length;
  const deliveryRate = totalDelivered + totalFailed > 0
    ? ((totalDelivered / (totalDelivered + totalFailed)) * 100).toFixed(1)
    : '0';

  const handleToggle = (id: string) => {
    setSubs(prev => prev.map(s => s.id === id
      ? { ...s, status: s.status === 'active' ? 'paused' : 'active' }
      : s
    ));
  };

  const handleRetry = (id: string) => {
    setEvents(prev => prev.map(e => e.id === id
      ? { ...e, status: 'retrying' as DeliveryStatus, attemptCount: e.attemptCount + 1, nextRetryAt: '2026-08-21 01:20:00' }
      : e
    ));
  };

  const filteredSubs = subs.filter(s => {
    const matchSearch = s.subscriberName.toLowerCase().includes(search.toLowerCase()) || s.endpointUrl.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || s.subscriberRole === roleFilter;
    const matchStream = streamFilter === 'all' || s.streams.includes(streamFilter);
    return matchSearch && matchRole && matchStream;
  });

  const filteredEvents = events.filter(e => {
    const matchStatus = statusFilter === 'all' || e.status === statusFilter;
    const matchStream = streamFilter === 'all' || e.streamType === streamFilter;
    return matchStatus && matchStream;
  });

  const failedCount = events.filter(e => e.status === 'failed').length;
  const retryingCount = events.filter(e => e.status === 'retrying').length;

  return (
    <div className="flex flex-col gap-6 p-6 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Radio size={20} className="text-primary" />
            <h1 className="text-xl font-bold text-foreground">Compliance Event Subscriptions</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage webhook subscriptions for regulators, custodians, and auditors across settlement, KYC, and AML event streams.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
        >
          <Plus size={16} />
          New Subscription
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Active Subscriptions" value={String(activeCount)} subValue={`of ${subs.length} total`} variant="info" icon={Activity} />
        <MetricCard label="Delivery Rate" value={`${deliveryRate}%`} subValue="Last 30 days" variant="positive" icon={CheckCircle} />
        <MetricCard label="Failed Deliveries" value={String(totalFailed)} subValue={`${failedCount} need retry`} variant={totalFailed > 0 ? 'critical' : 'default'} icon={AlertCircle} />
        <MetricCard label="Retrying Now" value={String(retryingCount)} subValue="Exponential backoff" variant={retryingCount > 0 ? 'warning' : 'default'} icon={RotateCcw} />
      </div>

      {/* Event Stream Cards */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Available Event Streams</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {eventStreams.map(s => <StreamCard key={s.id} stream={s} />)}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-3 rounded-xl p-1 w-fit">
        {([['subscriptions', 'Subscriptions'], ['delivery', 'Delivery Log']] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t ? 'bg-surface-2 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {label}
            {t === 'delivery' && failedCount > 0 && (
              <span className="ml-2 bg-critical/20 text-critical text-xs font-mono-data px-1.5 py-0.5 rounded-full">{failedCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Subscriptions Tab */}
      {tab === 'subscriptions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search subscribers or endpoints…"
                className="w-full bg-surface-3 border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              />
            </div>
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as SubscriberRole | 'all')}
              className="bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            >
              <option value="all">All Roles</option>
              <option value="regulator">Regulator</option>
              <option value="custodian">Custodian</option>
              <option value="auditor">Auditor</option>
            </select>
            <select
              value={streamFilter}
              onChange={e => setStreamFilter(e.target.value as StreamType | 'all')}
              className="bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            >
              <option value="all">All Streams</option>
              <option value="settlement">Settlement</option>
              <option value="kyc">KYC / KYB</option>
              <option value="aml">AML / CFT</option>
            </select>
          </div>

          {/* Subscription list */}
          <div className="space-y-3">
            {filteredSubs.length === 0 ? (
              <div className="glass-card rounded-xl p-8 text-center text-muted-foreground text-sm">No subscriptions match your filters.</div>
            ) : (
              filteredSubs.map(sub => (
                <SubscriptionRow key={sub.id} sub={sub} onToggle={handleToggle} />
              ))
            )}
          </div>
        </div>
      )}

      {/* Delivery Log Tab */}
      {tab === 'delivery' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as DeliveryStatus | 'all')}
              className="bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            >
              <option value="all">All Statuses</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="retrying">Retrying</option>
              <option value="pending">Pending</option>
            </select>
            <select
              value={streamFilter}
              onChange={e => setStreamFilter(e.target.value as StreamType | 'all')}
              className="bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
            >
              <option value="all">All Streams</option>
              <option value="settlement">Settlement</option>
              <option value="kyc">KYC / KYB</option>
              <option value="aml">AML / CFT</option>
            </select>
            {failedCount > 0 && (
              <button
                onClick={() => setEvents(prev => prev.map(e => e.status === 'failed' ? { ...e, status: 'retrying' as DeliveryStatus, attemptCount: e.attemptCount + 1, nextRetryAt: '2026-08-21 01:25:00' } : e))}
                className="flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 border border-accent/30 rounded-lg px-3 py-2 transition-colors"
              >
                <RotateCcw size={14} />
                Retry All Failed ({failedCount})
              </button>
            )}
          </div>

          {/* Table */}
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-3/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">Subscriber</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">Stream / Event</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">Attempts</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">HTTP</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">Latency / Error</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-widest text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground text-sm">No delivery events match your filters.</td>
                    </tr>
                  ) : (
                    filteredEvents.map(event => (
                      <DeliveryRow key={event.id} event={event} onRetry={handleRetry} />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && <AddSubscriptionModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
